import { useEffect, useState, useMemo, useCallback } from 'react';
import styles from './TeacherDetailView.module.scss';
import { ExtendedUnit, ExtendedHeaUnit } from '@types';
import FSBackIcon from 'src/assets/icons/arrow_back.svg?react';
import Button from '@src/components/button/Button';
import SidebarUnit from './SidebarUnit';
import SelectedContent from '@src/components/program-details/selected-content/SelectedContent';
import { useCourseContent } from '../../../../hooks/useCourseContent';
import { useAnalytics, Events, useUser } from '@hooks';
import { useMediaQuery } from 'react-responsive';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';

type Props = {
  unit: ExtendedUnit | ExtendedHeaUnit;
  onClose: () => void;
  goLessons: () => void;
};

function TeacherDetailView(props: Props) {
  const {
    unit,
    onClose,
    goLessons
  } = props;

  const {
    content: selectedContent,
    lesson,
    isParentApp,
    program,
  } = useCourseContent();

  const [showMenu, setShowMenu] = useState(false);
  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
  }, []);
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const handleMenu = useCallback(() => {
    if (isTablet || isMobile) {
      setShowMenu(current => !current)
    } else {
      onClose()
    }
  }, [isMobile, isTablet, onClose]);

  const listStyle = useMemo(() => {
    if (isTablet || isMobile) {
      return styles.listMenu;      
    } else {
      return styles.list;
    }   
  }, [isTablet, isMobile])

  const screenWidth = useMemo(() => (isTablet || isMobile) ? -window.innerWidth : 0, [isMobile, isTablet]);

  const handleCloseMenu = () => {
    setShowMenu(false)
  }

  const listSection = (
    <div className={listStyle} style={{transform: showMenu ? 'translateX(0)' : `translateX(${screenWidth}px)`}}>
      { (isTablet || isMobile) && showMenu && <div className={styles.menuBar}>
        <button onClick={handleMenu} className={styles.menuButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <CloseOutlined className={styles.burgerIcon}/>
          </div>
        </button>
        <button onClick={onClose} className={styles.closeButton} type={'button'}>
          <div className={styles.menuWrapper}>
          <FSBackIcon className={styles.closeIcon}/><div>Go Back</div>
          </div>
        </button>
      </div>}
      <SidebarUnit unit={unit as any} lastVisitedLesson={undefined} closeNav={handleCloseMenu}/>
    </div>
  );

  useEffect(() => {
    if (selectedContent) {
      trackEvent(Events.actions.courseMaterials, {
        course_name: program?.name,
        unit_name: unit?.name,
        module_name: selectedContent?.name,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  }, [program?.name, selectedContent, trackEvent, unit?.name, user]);

  return (
    <div className={styles.container}>
      {(isTablet || isMobile) && showMenu && <div className={styles.fsBackdrop} onClick={() => setShowMenu(false)}/>}
      <div className={styles.titleView}>
        {(isTablet || isMobile) && <button onClick={handleMenu} className={styles.menuButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <MenuOutlined className={styles.burgerIcon}/>
          </div>
        </button>}
        <button onClick={onClose} className={styles.closeButton} type={'button'}>
          <div className={styles.menuWrapper}>
            <FSBackIcon className={styles.closeIcon}/>
          </div>
        </button>
        <div className={styles.titleSubView}>
          <div className={styles.title}>Additional Materials</div>
        </div>
        <Button label='View Lessons' color='white' className={styles.viewLesson} onClick={goLessons} />
      </div>
      <div className={styles.page}>
        {listSection}
        <div className={styles.content}>
          {selectedContent && <SelectedContent content={selectedContent} lesson={lesson} unit={unit} goBack={onClose} isPreview={true} isParentApp={isParentApp} />}
        </div>
      </div>
    </div>
  );
}

export default TeacherDetailView;
